import { useState, useEffect } from 'react';
import { Download, Loader2, X, CheckCircle2, AlertCircle, Link2 } from 'lucide-react';

interface KpWorkItem {
  external_id?: string;
  company_name: string;
  job_title: string;
  start_year?: number;
  start_month?: number;
  end_year?: number;
  end_month?: number;
  employment_type?: string;
  part_time_percentage?: number;
  responsibilities?: string;
  from_partner: boolean;
}

interface Props {
  onImported?: () => void; // callback to refresh parent career_history list
}

/**
 * Lists work_experience entries from connected Karrierepasset profile that
 * are NOT already in user's career_history, with per-item import checkbox.
 *
 * Self-hides if user not linked.
 */
export function KarrierepassetImportWorkExperience({ onImported }: Props) {
  const [linked, setLinked] = useState<boolean | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<KpWorkItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user is linked
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/candidate/karrierepasset/status', { credentials: 'include' });
        if (res.status === 503) { setLinked(false); return; }
        const data = await res.json();
        setLinked(!!data.connected);
      } catch { setLinked(false); }
    })();
  }, []);

  async function handleOpen() {
    setLoading(true); setError(null); setSuccess(null); setItems([]);
    try {
      const res = await fetch('/api/candidate/karrierepasset/push-preview', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'reverify_required') {
          setError('Karrierepasset-koblingen må verifiseres på nytt. Gå til Innstillinger.');
        } else {
          setError(data.error || 'Kunne ikke hente data fra Karrierepasset');
        }
        return;
      }
      // Filter out items that came from us (from_partner=true) — those are duplicates
      const kpItems = (data.existing_on_kp.work_experience || []).filter((w: KpWorkItem) => !w.from_partner);
      setItems(kpItems);
      // Pre-select all by default
      setSelected(new Set(kpItems.map((w: KpWorkItem) => w.external_id || `${w.company_name}-${w.job_title}-${w.start_year}`)));
      setShowModal(true);
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setLoading(false); }
  }

  function toggleItem(key: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function getKey(item: KpWorkItem): string {
    return item.external_id || `${item.company_name}-${item.job_title}-${item.start_year}`;
  }

  async function handleImport() {
    setImporting(true); setError(null);
    try {
      const itemsToImport = items.filter(item => selected.has(getKey(item)));
      if (itemsToImport.length === 0) {
        setError('Ingen poster valgt');
        return;
      }
      const res = await fetch('/api/candidate/karrierepasset/import-work-experience', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToImport }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Import feilet'); return; }
      setSuccess(`Importert ${data.imported} jobberfaring (${data.skipped} hoppet over)`);
      setShowModal(false);
      setSelected(new Set());
      if (onImported) onImported();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setImporting(false); }
  }

  // Don't render anything if not linked
  if (linked !== true) return null;

  return (
    <>
      <button
        onClick={handleOpen}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200 disabled:opacity-50"
        title="Importer arbeidserfaring fra din Karrierepasset-profil"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
        Importer fra Karrierepasset
      </button>

      {success && (
        <div className="mt-2 text-sm text-green-700 inline-flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4" /> {success}
        </div>
      )}
      {error && !showModal && (
        <div className="mt-2 text-sm text-red-700 inline-flex items-center gap-1">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-purple-50 sticky top-0 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Download className="w-5 h-5 text-purple-600" />
                  Importer arbeidserfaring fra Karrierepasset
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Velg hvilke poster du vil kopiere til din karriereprofil hos oss.
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Ingen ny arbeidserfaring å importere fra Karrierepasset.</p>
                  <p className="text-xs mt-2">Alt som ligger på din KP-profil er enten allerede registrert her eller stammer opprinnelig fra Kompetanseattest.</p>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-600 italic">
                    Fant {items.length} poster på Karrierepasset som ikke ligger i din karriereprofil hos oss.
                  </div>

                  <div className="space-y-2">
                    {items.map(item => {
                      const key = getKey(item);
                      return (
                        <label key={key} className="flex items-start gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selected.has(key)}
                            onChange={() => toggleItem(key)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {item.job_title}
                              {item.company_name && <span className="text-gray-600"> @ {item.company_name}</span>}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {item.start_year && item.start_month && (
                                <>
                                  {String(item.start_month).padStart(2, '0')}/{item.start_year}
                                  {item.end_year ? ` → ${String(item.end_month || '').padStart(2, '0')}/${item.end_year}` : ' → pågående'}
                                </>
                              )}
                              {item.employment_type === 'part_time' && item.part_time_percentage && (
                                <span className="ml-2 text-purple-700">{item.part_time_percentage}%</span>
                              )}
                            </div>
                            {item.responsibilities && (
                              <div className="text-xs text-gray-600 mt-1 italic line-clamp-2">{item.responsibilities}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                    <strong>OBS:</strong> Importerte poster legges inn med en lenke til Karrierepasset (kp_external_id).
                    De får et generelt yrkeskode-felt som du senere kan oppdatere ved å redigere posten.
                  </div>
                </>
              )}

              <div className="flex gap-2 pt-2 border-t">
                {items.length > 0 && (
                  <button
                    onClick={handleImport}
                    disabled={importing || selected.size === 0}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Importer {selected.size} valgte
                  </button>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >Lukk</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
